import { formatEther, formatUnits, pad, encodePacked, type Address } from 'viem';

/**
 * Format token amount from bigint to human-readable string
 * @param amount - Token amount in smallest unit
 * @param decimals - Token decimals (default: 18)
 * @returns Formatted string with locale
 */
export const formatTokenAmount = (amount: bigint | undefined, decimals = 18): string => {
  if (!amount) return '0.00';
  const formatted = formatUnits(amount, decimals);
  const num = parseFloat(formatted);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

/**
 * Format ETH/native token amount
 * @param amount - Amount in wei
 * @returns Formatted string
 */
export const formatNativeAmount = (amount: bigint | undefined): string => {
  if (!amount) return '0.00';
  const formatted = formatEther(amount);
  const num = parseFloat(formatted);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

/**
 * Pad address to bytes32 format for LayerZero
 * LayerZero requires addresses to be padded to 32 bytes
 * @param address - Ethereum address
 * @returns Padded bytes32 address
 */
export const padAddressToBytes32 = (address: Address): `0x${string}` => {
  return pad(address, { size: 32 });
};

/**
 * Shorten address for display
 * @param address - Full address
 * @param startChars - Number of chars to show at start (default: 6)
 * @param endChars - Number of chars to show at end (default: 4)
 * @returns Shortened address (e.g., "0x1234...5678")
 */
export const shortenAddress = (
  address: string | undefined,
  startChars = 6,
  endChars = 4
): string => {
  if (!address) return '';
  if (address.length < startChars + endChars) return address;
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Format transaction hash for display
 * @param txHash - Transaction hash
 * @returns Shortened tx hash
 */
export const formatTxHash = (txHash: string | undefined): string => {
  return shortenAddress(txHash, 10, 8);
};

/**
 * Check if user has sufficient balance
 * @param balance - User's balance
 * @param requiredAmount - Required amount
 * @returns True if balance is sufficient
 */
export const hasSufficientBalance = (
  balance: bigint | undefined,
  requiredAmount: bigint
): boolean => {
  if (!balance) return false;
  return balance >= requiredAmount;
};

/**
 * Check if amount is valid (greater than 0)
 * @param amount - Amount string
 * @returns True if valid
 */
export const isValidAmount = (amount: string): boolean => {
  const num = parseFloat(amount);
  return !isNaN(num) && num > 0;
};

/**
 * Truncate decimal places to a maximum number (NO rounding, just cut off)
 * This is critical for bridges that only support limited decimal precision
 * @param value - Number string to truncate
 * @param maxDecimals - Maximum decimal places (default: 6)
 * @returns Truncated string
 * @example
 * truncateDecimals('123.123456789', 6) → '123.123456'
 * truncateDecimals('123.1', 6) → '123.1'
 * truncateDecimals('123', 6) → '123'
 */
export const truncateDecimals = (value: string, maxDecimals = 6): string => {
  if (!value || value === '') return value;

  // Split by decimal point
  const parts = value.split('.');

  // No decimal point - return as is
  if (parts.length === 1) return value;

  // Has decimal point - truncate to max decimals
  const [integerPart, decimalPart] = parts;

  // If decimal part is shorter than max, return as is
  if (decimalPart.length <= maxDecimals) return value;

  // Truncate (don't round!) decimal part
  const truncatedDecimals = decimalPart.slice(0, maxDecimals);

  return `${integerPart}.${truncatedDecimals}`;
};

/**
 * Get LayerZero scan URL for transaction tracking
 * @param txHash - Transaction hash
 * @returns LayerZero scan URL
 */
export const getLayerZeroScanUrl = (txHash: string): string => {
  return `https://layerzeroscan.com/tx/${txHash}`;
};

/**
 * Build LayerZero V2 Options with executor gas limit
 * Equivalent to: Options.newOptions().addExecutorLzReceiveOption(gasLimit, value).toBytes()
 *
 * LayerZero V2 Options format:
 * - uint16: Option type (3 = EXECUTOR_LZ_RECEIVE_OPTION)
 * - uint16: Option length (always 16 bytes for type 3)
 * - uint128: Gas limit for destination chain execution
 * - uint128: Value (native token amount to send, usually 0)
 *
 * @param gasLimit - Gas limit for destination chain (e.g., 200000)
 * @param value - Native token value to forward (default: 0)
 * @returns Encoded options as hex bytes
 */
export const buildLzOptions = (gasLimit: number, value: number = 0): `0x${string}` => {
  const OPTION_TYPE_3 = 3; // EXECUTOR_LZ_RECEIVE_OPTION
  const OPTION_LENGTH = 32; // Length of gas limit (16 bytes) + value (16 bytes)

  return encodePacked(
    ['uint16', 'uint16', 'uint128', 'uint128'],
    [OPTION_TYPE_3, OPTION_LENGTH, BigInt(gasLimit), BigInt(value)]
  );
};
